<!-- src/views/password/ForgotPasswordView.vue -->
<template>
  <div class="min-h-screen flex flex-col">
    <!-- Background Patterns -->
    <BackgroundPatterns />

    <!-- Minimal Header -->
    <header
      class="fixed inset-x-0 top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200/50"
    >
      <div class="max-w-8xl mx-auto px-4 sm:px-6 py-3">
        <div class="flex items-center justify-between">
          <router-link
            to="/"
            class="flex items-center h-8 hover:scale-105 transition-transform"
          >
            <img
              :src="systemLogo"
              alt="Aberdeen Institute"
              class="h-full w-auto object-contain"
            />
          </router-link>
          <div class="flex items-center gap-2">
            <LanguageSelector />
            <StatusIndicator class="hidden sm:inline-flex" />
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content: Centered Layout -->
    <main
      class="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 py-8 min-h-[calc(100vh-4rem)]"
    >
      <div class="w-full max-w-md sm:max-w-lg">
        <!-- Back Button -->
        <div class="mb-6">
          <router-link
            to="/login"
            class="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon class="h-4 w-4 mr-2" />
            {{ t("common.back") }}
          </router-link>
        </div>

        <!-- Welcome Title -->
        <div class="text-center mb-8">
          <h2
            class="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3"
          >
            {{ t("auth.forgotPassword") }}
          </h2>
          <p class="text-sm sm:text-base text-gray-500">
            {{ getStepSubtitle }}
          </p>
        </div>

        <!-- Login Form -->
        <div
          class="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 sm:p-10"
        >
          <!-- Step 1: Username and Email Verification -->
          <form
            v-if="currentStep === 0"
            @submit.prevent="onStep1Finish"
            class="space-y-5"
          >
            <!-- Username Field -->
            <div>
              <label
                for="username"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.username") }}
              </label>
              <div class="relative">
                <input
                  id="username"
                  v-model="formState.username"
                  name="username"
                  type="text"
                  autocomplete="username"
                  required
                  :disabled="loading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('form.enterUsername')"
                />
                <UserIcon
                  class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                />
              </div>
              <p class="mt-1 text-xs text-gray-500">
                {{ t("form.usernameHelp") }}
              </p>
            </div>

            <!-- Email Field -->
            <div>
              <label
                for="email"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.email") }}
              </label>
              <div class="relative">
                <input
                  id="email"
                  v-model="formState.email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  :disabled="loading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('auth.enterEmail')"
                />
                <EnvelopeIcon
                  class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                />
              </div>
            </div>

            <!-- Submit Button -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="loading || !formState.username || !formState.email"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:from-blue-400 disabled:to-blue-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <LoadingSpinner
                  v-if="loading"
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <ArrowRightIcon v-else class="mr-2 h-5 w-5 relative z-10" />
                <span class="relative z-10">{{ t("common.next") }}</span>
              </button>
            </div>
          </form>

          <!-- Step 2: Verification Code and New Password -->
          <form
            v-if="currentStep === 1"
            @submit.prevent="onStep2Finish"
            class="space-y-5"
          >
            <!-- Verification Code Field -->
            <div>
              <label
                for="token"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.verificationCode") }}
              </label>
              <div class="relative">
                <input
                  id="token"
                  v-model="formState.token"
                  name="token"
                  type="text"
                  autocomplete="one-time-code"
                  required
                  :disabled="loading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('auth.enterVerificationCode')"
                />
                <KeyIcon
                  class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                />
              </div>
            </div>

            <!-- New Password Field -->
            <div>
              <label
                for="password"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.password") }}
              </label>
              <div class="relative">
                <input
                  id="password"
                  v-model="formState.password"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  autocomplete="new-password"
                  required
                  :disabled="loading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 pr-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('auth.enterPassword')"
                />
                <LockClosedIcon
                  class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon v-if="showPassword" class="h-5 w-5" />
                  <EyeSlashIcon v-else class="h-5 w-5" />
                </button>
              </div>

              <!-- Password Requirements -->
              <div
                v-if="formState.password"
                class="mt-2 p-3 bg-gray-50 rounded-lg"
              >
                <div class="space-y-1 text-xs">
                  <div class="flex items-center">
                    <CheckCircleIcon
                      v-if="formState.password.length >= 8"
                      class="h-3 w-3 text-green-500 mr-1"
                    />
                    <XCircleIcon v-else class="h-3 w-3 text-red-500 mr-1" />
                    <span
                      :class="
                        formState.password.length >= 8
                          ? 'text-green-700'
                          : 'text-red-700'
                      "
                    >
                      {{ isZhLocale ? "至少8个字符" : "At least 8 characters" }}
                    </span>
                  </div>
                  <div class="flex items-center">
                    <CheckCircleIcon
                      v-if="
                        /^(?=.*[0-9])(?=.*[a-zA-Z])/.test(formState.password)
                      "
                      class="h-3 w-3 text-green-500 mr-1"
                    />
                    <XCircleIcon v-else class="h-3 w-3 text-red-500 mr-1" />
                    <span
                      :class="
                        /^(?=.*[0-9])(?=.*[a-zA-Z])/.test(formState.password)
                          ? 'text-green-700'
                          : 'text-red-700'
                      "
                    >
                      {{
                        isZhLocale
                          ? "包含字母和数字"
                          : "Contains letters and numbers"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Confirm Password Field -->
            <div>
              <label
                for="confirmPassword"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.confirmPassword") }}
              </label>
              <div class="relative">
                <input
                  id="confirmPassword"
                  v-model="formState.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  name="confirmPassword"
                  autocomplete="new-password"
                  required
                  :disabled="loading"
                  class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 pr-10 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  :placeholder="t('auth.enterConfirmPassword')"
                />
                <LockClosedIcon
                  class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon v-if="showConfirmPassword" class="h-5 w-5" />
                  <EyeSlashIcon v-else class="h-5 w-5" />
                </button>
              </div>

              <!-- Password Match Indicator -->
              <div v-if="formState.confirmPassword" class="mt-1">
                <div
                  v-if="formState.password === formState.confirmPassword"
                  class="flex items-center text-xs text-green-700"
                >
                  <CheckCircleIcon class="h-3 w-3 mr-1" />
                  {{ isZhLocale ? "密码匹配" : "Passwords match" }}
                </div>
                <div v-else class="flex items-center text-xs text-red-700">
                  <XCircleIcon class="h-3 w-3 mr-1" />
                  {{ isZhLocale ? "密码不匹配" : "Passwords do not match" }}
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="loading || !isFormValid"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-green-700 hover:to-green-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:from-green-400 disabled:to-green-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <LoadingSpinner
                  v-if="loading"
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <CheckIcon v-else class="mr-2 h-5 w-5 relative z-10" />
                <span class="relative z-10">{{ t("common.submit") }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { notificationService } from "@/services/notificationService";
import * as Sentry from "@sentry/vue";
import {
  UserIcon,
  EnvelopeIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import BackgroundPatterns from "@/components/layout/BackgroundPatterns.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import StatusIndicator from "@/components/common/StatusIndicator.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { systemConfigService } from "@/services/systemConfigService";

const router = useRouter();
const { t, locale } = useI18n();
const authStore = useAuthStore();
const loading = ref(false);
const currentStep = ref(0);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

// Computed properties
const isZhLocale = computed(
  () => locale.value === "zh-CN" || locale.value === "zh",
);

const isFormValid = computed(() => {
  if (currentStep.value === 0) {
    return formState.username && formState.email;
  } else {
    return (
      formState.token &&
      formState.password &&
      formState.confirmPassword &&
      formState.password === formState.confirmPassword &&
      formState.password.length >= 8 &&
      /^(?=.*[0-9])(?=.*[a-zA-Z])/.test(formState.password)
    );
  }
});

const formState = reactive({
  username: "",
  email: "",
  token: "",
  password: "",
  confirmPassword: "",
});

// Get current step subtitle
const getStepSubtitle = computed(() => {
  switch (currentStep.value) {
    case 0:
      return t("auth.enterUsernameEmail");
    case 1:
      return t("auth.enterVerificationCodeAndPassword");
    default:
      return "";
  }
});

// 步骤1：请求密码重置
const onStep1Finish = async () => {
  try {
    loading.value = true;
    await authStore.handlePasswordResetRequest(
      formState.username,
      formState.email,
    );
    currentStep.value = 1;
  } catch (error: any) {
    console.error("Password reset request error:", error);
    if (error.response?.status !== 401) {
      Sentry.captureException(error, {
        tags: {
          component: "ForgotPasswordView",
          action: "requestReset",
        },
        extra: {
          username: formState.username,
          email: formState.email,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        },
      });
    }

    if (error.response?.status === 404) {
      notificationService.error("auth.userNotFound");
    } else if (error.response?.status === 400) {
      notificationService.error("auth.invalidEmailOrUsername");
    } else {
      notificationService.error("common.systemError");
    }
  } finally {
    loading.value = false;
  }
};

// 步骤2：验证码验证并重置密码
async function onStep2Finish() {
  if (!formState.username) return;

  try {
    loading.value = true;
    await authStore.handlePasswordReset(
      formState.username,
      formState.token,
      formState.password,
    );
    notificationService.success("auth.passwordResetSuccess");

    try {
      await router.push("/login");
    } catch (redirectError) {
      console.error("Redirect error:", redirectError);
      Sentry.captureException(redirectError, {
        tags: {
          component: "ForgotPasswordView",
          action: "redirectAfterReset",
        },
        extra: {
          targetPath: "/login",
          currentPath: window.location.pathname,
        },
      });
      // 如果路由跳转失败，使用 window.location
      window.location.href = "/login";
    }
  } catch (error: any) {
    console.error("Password reset error:", error);
    Sentry.captureException(error, {
      tags: {
        component: "ForgotPasswordView",
        action: "resetPassword",
      },
      extra: {
        username: formState.username,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      },
    });

    if (error.response?.status === 400) {
      notificationService.error("auth.invalidVerificationCode");
    } else if (error.response?.status === 404) {
      notificationService.error("auth.resetRequestExpired");
    } else {
      notificationService.error("common.systemError");
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Ensure main content is above background */
main {
  position: relative;
  z-index: 10;
}

/* Enhanced focus states */
input:focus-visible {
  outline: 2px solid rgb(59, 130, 246);
  outline-offset: 2px;
}

button:focus-visible {
  outline: 2px solid rgb(59, 130, 246);
  outline-offset: 2px;
}

/* Button hover effects */
button[type="submit"]:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -5px rgba(59, 130, 246, 0.4);
}

button[type="submit"]:active:not(:disabled) {
  transform: translateY(0);
}

/* Green button for step 2 */
button[type="submit"]:hover:not(:disabled).bg-gradient-to-r.from-green-600 {
  box-shadow: 0 12px 28px -5px rgba(34, 197, 94, 0.4);
}

/* Smooth transitions */
input,
button {
  transition: all 0.2s ease;
}

/* Header backdrop blur enhancement */
header {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 50;
}

/* Form card enhanced glass morphism effect */
.bg-white\/80 {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 8px 32px -8px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Input field enhancements */
input {
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

input:hover {
  border-color: rgb(147, 197, 253);
  background-color: rgba(255, 255, 255, 0.9);
}

input:focus {
  background-color: rgba(255, 255, 255, 0.95);
}

/* WebKit autofill styling */
input:-webkit-autofill {
  transition: background-color 50000s ease-in-out 0s;
  -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.9) inset !important;
  -webkit-text-fill-color: rgb(17, 24, 39) !important;
}

/* Improved button states */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .max-w-md {
    max-width: 100%;
    padding: 0 1rem;
  }

  .bg-white\/80 {
    margin: 0 0.5rem;
    padding: 2rem 1.5rem !important;
  }

  /* Mobile: Adjust main container */
  main {
    min-height: calc(100vh - 3rem);
    padding: 1rem 1rem 2rem 1rem;
  }

  /* Mobile: Adjust spacing */
  .mb-8 {
    margin-bottom: 1.5rem;
  }

  /* Mobile: Adjust input fields */
  input {
    font-size: 16px; /* Prevent zoom on iOS */
  }
}

/* Subtle animation for the form card */
.bg-white\/80 {
  animation: slideIn 0.6s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
